import { Injectable, inject } from '@angular/core';
import { DateTime, Duration } from 'luxon';

// eslint-disable-next-line no-restricted-imports
import { LocalizedDatePipe } from '@shared/localized-date.pipe';

import { TimeFilter, TimeFilterRange, TimeFilterType } from './api/model';

type TimeRange = {
  start: DateTime;
  end: DateTime | null;
};

export type TimeRangeFormat = {
  start: string;
  end: string | null;
};

/**
 * Resolve a TimeFilter object from the backend.
 */
@Injectable()
export class TimeFilterService {
  private localizedDate = inject(LocalizedDatePipe);

  // should match backend implementation of TimeFilter#startInDays(LocalDate)
  private readonly defaultDuration = Duration.fromObject({ weeks: 4 });

  public formatTimeFilter(timeFilter: TimeFilter | null): TimeRangeFormat | null {
    if (!timeFilter) {
      return null;
    }

    const timeRange = this.timeFilterToTimeRange(timeFilter);
    return {
      start: this.localizedDate.transform(timeRange.start, DateTime.DATE_MED),
      end: timeRange.end ? this.localizedDate.transform(timeRange.end, DateTime.DATE_MED) : null,
    };
  }

  private timeFilterToTimeRange(timeFilter: TimeFilter): TimeRange {
    if (timeFilter.type === TimeFilterType.DATE) {
      return this.dateFilterToTimeRange(timeFilter.start, timeFilter.end);
    } else if (timeFilter.type === TimeFilterType.RANGE) {
      return this.rangeFilterToTimeRange(timeFilter.range);
    } else {
      console.error('unsupported time filter type', timeFilter.type);
      return {
        start: DateTime.now().minus(this.defaultDuration),
        end: null,
      };
    }
  }

  private dateFilterToTimeRange(start: string | undefined, end: string | undefined): TimeRange {
    if (!start) {
      return {
        start: DateTime.now().minus(this.defaultDuration),
        end: null,
      };
    }

    return {
      start: DateTime.fromFormat(start, 'yyyyMMdd'),
      end: end ? DateTime.fromFormat(end, 'yyyyMMdd') : null,
    };
  }

  private rangeFilterToTimeRange(range: TimeFilterRange | undefined): TimeRange {
    let duration: Duration;
    switch (range) {
      case TimeFilterRange.WEEK_1:
      case TimeFilterRange.WEEK_2:
      case TimeFilterRange.WEEK_3:
        duration = Duration.fromObject({ weeks: range });
        break;
      case TimeFilterRange.MONTH_1:
      case TimeFilterRange.MONTH_2:
      case TimeFilterRange.MONTH_3:
      case TimeFilterRange.MONTH_4:
      case TimeFilterRange.MONTH_5:
      case TimeFilterRange.MONTH_6:
        duration = Duration.fromObject({ months: range - TimeFilterRange.WEEK_3 });
        break;
      default:
        console.error('unsupported time filter range value: ', range);
        duration = this.defaultDuration;
    }

    return {
      start: DateTime.now().minus(duration),
      end: null,
    };
  }
}
